import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, FormHelperText, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { PlatformPeopleService } from '../../../../../Component/Api/PlatformPeople/PlatformPeopleService';
import AppCheckbox from '../../../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { AppDatepicker } from '../../../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppSelectList, { IDropDownOptions } from '../../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { genderList, raceList } from '../../../../../Component/Common/Constants/Lists/DropdownListOptions';
import { PlatformPeople, ValidateUniquePerson } from '../../../../../Component/Model/Dto/Platform/PlatformPeople';
import { EditPersonalInformationValidationSchema } from './EditPersonalInformationValidationSchema';
import { useRecoilValue } from 'recoil';
import { userRolesState } from '../../../../../State/user.Atom';
import { PlatformPeopleEditRole, UserAdminRole } from '../../../../../Component/Common/Constants/UserRoles';
import { getDateWithoutTime } from '../../../../../Helpers/DateFormatter';

interface IProps {
    nationalityOptions: IDropDownOptions[];
    personData: PlatformPeople;
    personId: string;
}
export const EditPersonalInformation = ({ nationalityOptions, personData, personId }: IProps) => {
    const userRoles = useRecoilValue(userRolesState);
    const [showIdNumberError, setShowIdNumberError] = useState<boolean>(false);
    const [showEmployeeCodeError, setShowEmployeeCodeError] = useState<boolean>(false);
    const [idNumber, setIdNumber] = useState<string>(personData.idNumber);
    const [employeeCode, setEmployeeCode] = useState<string>(personData.employeeCode);
    const editRoleAccess = [
        PlatformPeopleEditRole,
        UserAdminRole
    ]

    const isEditable = userRoles.some(r => editRoleAccess.includes(r));

    const { control, handleSubmit, formState: { isSubmitting }, reset } = useForm({
        mode: 'all',
        resolver: yupResolver(EditPersonalInformationValidationSchema),
    });

    useEffect(() => {
        fetchPersonInformation()
        reset(personData)
    }, [personId])

    const fetchPersonInformation = async () => {
        try {
            const result = await PlatformPeopleService.getById(personId);
            if (result.data) {
                reset(result.data)
            }
        } catch (error) {
            console.error(error)
        }
    }

    const validateIdNumber = async () => {
        setShowIdNumberError(false);
        setShowEmployeeCodeError(false);

        if (idNumber != null && idNumber !== "" && idNumber !== personData.idNumber) {
            let request: ValidateUniquePerson = {
                value: idNumber,
                organisationId: personData.organisationId!
            };

            let idNumberExist = await PlatformPeopleService.idNumberExist(request);
            setShowIdNumberError(idNumberExist.data);
        }

        if (idNumber == "") {
            validateEmployeeCode();
        }
    }

    const validateEmployeeCode = async () => {
        setShowEmployeeCodeError(false);

        if (employeeCode != null && employeeCode != "" && employeeCode !== personData.employeeCode && (idNumber == null || idNumber == "")) {
            let request: ValidateUniquePerson = {
                value: employeeCode,
                organisationId: personData.organisationId!
            };

            let employeeCodeExist = await PlatformPeopleService.employeeCodeExist(request);
            setShowEmployeeCodeError(employeeCodeExist.data);
        }
    }

    const idNumberChanged = async (data: FieldValues) => {
        setIdNumber(data.currentTarget.value);
    }

    const employeeCodeChanged = async (data: FieldValues) => {
        setEmployeeCode(data.currentTarget.value);
    }

    const handleDataSubmit = async (data: FieldValues) => {
        if (!showEmployeeCodeError && !showIdNumberError) {
            try {
                const request: PlatformPeople = {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    idNumber: data.idNumber,
                    raceId: data.raceId,
                    dateOfBirth: data.dateOfBirth,
                    employeeCode: data.employeeCode,
                    genderId: data.genderId,
                    disabled: data.disabled,
                    nationalityId: data.nationalityId,
                    organisationId: personData.organisationId!
                }
                const result = await PlatformPeopleService.updatePlatformPerson(personId, request);
                if (result.data) {
                    toast.success('Person updated successfully');
                    fetchPersonInformation();
                }
            } catch (error: any) {
                console.log(error);
                toast.error(error.response.data);
            }
        }
    }

    return (
        <>
            <Grid item xs={12} container spacing={3} component="form" id='personForm' onSubmit={handleSubmit(handleDataSubmit)}>
                <Grid item xs={6}>
                    <Box mt={2}>
                        <AppTextInput control={control} name="firstName" label="First name *"  disabled={!isEditable}/>
                    </Box>
                    <Box mt={2}>
                        <AppTextInput control={control} name="idNumber" label="ID number or Passport" onChange={idNumberChanged} onBlur={validateIdNumber} showError={showIdNumberError}   disabled={!isEditable}/>
                        {showIdNumberError &&
                            <FormHelperText sx={{ color: 'red' }}>This ID number or Passport already exists within this organisation</FormHelperText>
                        }
                    </Box>
                    <Box mt={2}>
                        <AppSelectList control={control} name="raceId" label="Race *" items={raceList}  disabled={!isEditable}/>
                    </Box>
                    <Box mt={2}>
                        <AppDatepicker control={control} name="dateOfBirth" label="Date Of Birth" inputFormat='yyyy/MM/dd' maxDate={getDateWithoutTime(new Date())}  disabled={!isEditable}/>
                    </Box>
                    <Box mt={2}>
                        <AppCheckbox control={control} name='disabled' label='Disabled' disabled={!isEditable} />
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box mt={2}>
                        <AppTextInput control={control} name="lastName" label="Surname *"  disabled={!isEditable}/>
                    </Box>
                    <Box mt={2}>
                        <AppTextInput control={control} name="employeeCode" label="Employee Code" onChange={employeeCodeChanged} onBlur={validateEmployeeCode} showError={showEmployeeCodeError}  disabled={!isEditable}/>
                        {showEmployeeCodeError &&
                            <FormHelperText sx={{ color: 'red' }}>This Employee Code already exists within this organisation</FormHelperText>
                        }
                    </Box>
                    <Box mt={2}>
                        <AppSelectList control={control} name="genderId" label="Gender *" items={genderList}  disabled={!isEditable}/>
                    </Box>
                    <Box mt={2}>
                        <AppSelectList control={control} name="nationalityId" label="Nationality" items={nationalityOptions}  disabled={!isEditable}/>
                    </Box>
                </Grid>
            </Grid>
            <Grid item xs={12} container spacing={3} justifyContent={'end'}>
                <Grid item xs={6} sx={{ flex: 'revert !important' }}>
                    <Box mt={2}>
                        {isEditable &&
                            <LoadingButton loading={isSubmitting} variant='contained' form='personForm' type="submit" color='primary' sx={{ mr: 1 }}>
                                Save
                            </LoadingButton>
                        }
                    </Box>
                </Grid>
            </Grid>
        </>
    )
}

